/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from "../button/button";
import { PaymentCycle } from "../../models/enumerations/payment-cycle";
import React, { useEffect, useState } from "react";
import { query, collection, where, getDocs, getFirestore, DocumentData } from 'firebase/firestore';
import { Modal } from "../modal/modal";
import EnterpriseContactUs from "./enterprise-contact-us";
import { Carousel } from "react-responsive-carousel"
import { PlanId } from "../../models/enumerations/plan-id";
import { AddOnId } from "../../models/enumerations/add-on-id";
import PlansTableContent from "./plans-table-content";
import { useTranslation } from "react-i18next";
import FaqAccordion from "../faq-accordion/faq-accordion";
import Badge from "../badge/badge";
import AppContentTypeToggle from "../app-content-type-toggle/app-content-type-toggle";
import { PlanType } from "../../models/enumerations/plan-type";
import { UserLimit } from "../../models/enumerations/user-limit";
import { ReactComponent as CheckIcon } from "../../assets/icons/solid/check-circle.svg";
import { ReactComponent as CrossIcon } from "../../assets/icons/solid/cross-circle.svg";
import { LicenseLimit } from "../../models/enumerations/license-limit";
import Reviews from "../reviews/reviews";
import Accordion from "../accordion/accordion";
import { authLogout, useAuthState } from "../../utilities/contexts/auth-state-context";
import { OrganizationSubscription } from "../../models/interfaces/organization-subscription";

enum SubscriptionCarouselIndex {
  Trial,
  Core,
  Pro,
  Enterprise,
}

interface PlanData {
  planType: PlanType;
  carouselIndex: SubscriptionCarouselIndex;
  isCurrent: boolean;
  label: string;
  description: string;
  color: string;
  price: {
    [PaymentCycle.Monthly]: string;
    [PaymentCycle.Quarterly]: string;
    [PaymentCycle.Yearly]: string;
  };
  numberUsers: string;
  buttonText: string;
  buttonTextTable?: string;
  onButtonClick?: () => void;
  productToSelect?: DocumentData;
  benefitsTitle: string;
  benefits: {
    icon: React.ReactElement;
    text: string;
  }[];
}

interface PlansProps {
  onProductSelect: (data: any) => void;
  isUpgrade: boolean;
  isLeadCapture?: boolean;
  orgSub?: OrganizationSubscription;
}

const COMPONENT_CLASS = "c-subscriptions";

const Plans: React.FC<PlansProps> = ({ onProductSelect, isUpgrade, isLeadCapture, orgSub }) => {

  const { t } = useTranslation();
  const { state, setState } = useAuthState();

  const currentPlan = state?.organization?.plan?.value;
  const currentPaymentCycle = orgSub?.paymentCycle?.value;

  const [paymentCycle, setPaymentCycle] = useState<PaymentCycle>(PaymentCycle.Monthly);
  const [contactUsEnterpriseOpen, setContactUsEnterpriseOpen] = useState(false);
  const [addOnPrices, setAddOnPrices] = useState<{ user?: string, license?: string, organization?: string }>();
  const [carouselItemIndex, setCarouselItemIndex] = useState(SubscriptionCarouselIndex.Trial);
  const [coreData, setCoreData] = useState<PlanData>();
  const [proData, setProData] = useState<PlanData>();

  const checkmarkElement = <CheckIcon style={{ fill: "#00DFBC", color: "white" }} width="16px" height="16px" />;
  const crossElement = <CrossIcon width="16px" height="16px" />;

  let trialDataButtonText = t('pricing.plansPage.cards.trial.btn_text');
  let coreDataButtonText = t('buttons.btn_getStarted');
  let proDataButtonText = t('buttons.btn_getStarted');
  let enterpriseDataButtonText = t('buttons.btn_contactUs');

  let coreDataOnButtonClick: (() => void) | undefined = undefined;
  let proDataOnButtonClick: (() => void) | undefined = undefined;

  if (isUpgrade && orgSub) {
    if (currentPlan === PlanType.Trial) {
      coreDataButtonText = t('buttons.btn_upgrade');
      proDataButtonText = t('buttons.btn_upgrade');
    }
    else if (currentPlan === PlanType.Core) {
      coreDataButtonText = t('buttons.btn_currentPlan');
      proDataButtonText = t('buttons.btn_upgrade');
    }
    else if (currentPlan === PlanType.Business) {
      coreDataButtonText = t('buttons.btn_downgrade');
      proDataButtonText = t('buttons.btn_upgrade');
    }
    else if (currentPlan === PlanType.Pro) {
      coreDataButtonText = t('buttons.btn_downgrade');
      proDataButtonText = t('buttons.btn_currentPlan');
    }
    else if (currentPlan === PlanType.Enterprise) {
      coreDataButtonText = t('buttons.btn_contactUs');
      proDataButtonText = t('buttons.btn_contactUs');
      enterpriseDataButtonText = t('buttons.btn_currentPlan');

      coreDataOnButtonClick = () => setContactUsEnterpriseOpen(true);
      proDataOnButtonClick = () => setContactUsEnterpriseOpen(true);
    }
  }

  const trialData: PlanData | undefined =
    isUpgrade
      ? undefined
      : {
        planType: PlanType.Trial,
        carouselIndex: SubscriptionCarouselIndex.Trial,
        isCurrent: currentPlan === PlanType.Trial && currentPaymentCycle === paymentCycle,
        label: t('general.plan.trial'),
        description: t('pricing.plansPage.cards.trial.description'),
        color: "rgba(18, 22, 34, 0.05)",
        price: {
          [PaymentCycle.Monthly]: "Free",
          [PaymentCycle.Quarterly]: "Free",
          [PaymentCycle.Yearly]: "Free",
        },
        numberUsers: t('pricing.plansPage.cards.trial.numberUsers', { numberUsers: UserLimit.Trial }),
        buttonText: trialDataButtonText,
        buttonTextTable: t('buttons.btn_getStarted'),
        onButtonClick: () => { onProductSelect('STARTER') },
        benefitsTitle: t('pricing.plansPage.cards.trial.benefitsTitle'),
        benefits: [
          {
            icon: checkmarkElement,
            text: t('pricing.plansPage.cards.benefits.users', { numberUsers: UserLimit.Trial }),
          },
          {
            icon: checkmarkElement,
            text: t('pricing.plansPage.cards.benefits.wellnessPrograms', { numberWellnessPrograms: LicenseLimit.Trial }),
          },
          {
            icon: checkmarkElement,
            text: t('pricing.plansPage.cards.benefits.mobile_app'),
          },
          {
            icon: checkmarkElement,
            text: t('pricing.plansPage.cards.benefits.reports'),
          },
          {
            icon: checkmarkElement,
            text: t('pricing.plansPage.cards.benefits.platform'),
          },
          {
            icon: crossElement,
            text: t('pricing.plansPage.cards.benefits.add_ons'),
          },
          {
            icon: crossElement,
            text: t('pricing.plansPage.cards.benefits.course'),
          },
          {
            icon: crossElement,
            text: t('pricing.plansPage.cards.benefits.integrations'),
          },
          {
            icon: crossElement,
            text: t('pricing.plansPage.cards.benefits.support'),
          },
          {
            icon: crossElement,
            text: t('pricing.plansPage.cards.benefits.customer_success_manager'),
          },
        ],
      };

  const enterpriseData: PlanData = {
    planType: PlanType.Enterprise,
    carouselIndex: SubscriptionCarouselIndex.Enterprise,
    isCurrent: currentPlan === PlanType.Enterprise && currentPaymentCycle === paymentCycle,
    label: t('general.plan.enterprise'),
    description: t('pricing.plansPage.cards.enterprise.description'),
    color: "#ADDFFD",
    price: {
      [PaymentCycle.Monthly]: "Custom",
      [PaymentCycle.Quarterly]: "Custom",
      [PaymentCycle.Yearly]: "Custom",
    },
    numberUsers: t('pricing.general.startingAtUsers', { numberUsers: UserLimit.Enterprise }),
    buttonText: enterpriseDataButtonText,
    onButtonClick: () => setContactUsEnterpriseOpen(true),
    benefitsTitle: t('pricing.plansPage.cards.enterprise.benefitsTitle'),
    benefits: [
      {
        icon: checkmarkElement,
        text: t('pricing.general.startingAtUsers', { numberUsers: UserLimit.Enterprise }),
      },
      {
        icon: checkmarkElement,
        text: t("pricing.plansPage.cards.benefits.custom_pricing_users")
      },
      {
        icon: checkmarkElement,
        text: t("pricing.plansPage.cards.benefits.custom_pricing_wellness")
      },
      {
        icon: checkmarkElement,
        text: t("pricing.plansPage.cards.benefits.custom_integrations")
      },
      {
        icon: checkmarkElement,
        text: t("pricing.plansPage.cards.benefits.custom_staff_education")
      },
      {
        icon: checkmarkElement,
        text: t("pricing.plansPage.cards.benefits.priority_onboarding")
      },
      {
        icon: checkmarkElement,
        text: t('pricing.plansPage.cards.benefits.support'),
      },
      {
        icon: checkmarkElement,
        text: t('pricing.plansPage.cards.benefits.customer_success_manager'),
      },
      {
        icon: checkmarkElement,
        text: t('pricing.plansPage.cards.benefits.api_integration'),
      },
    ],
  };

  const [cards, setCards] = useState<{ element: React.ReactElement, planType: PlanType }[]>([]);
  const [planDataArr, setPlanDataArr] = useState<(PlanData | undefined)[]>([]);

  const selectProduct = (product: DocumentData) => {
    let productTemp = { ...product };
    // if (productTemp && paymentCycle === PaymentCycle.Monthly)
    //   productTemp.priceInfo = product?.priceInfo.find((item: any) => item.interval === "month");
    // else if (productTemp && paymentCycle === PaymentCycle.Yearly)
    //   productTemp.priceInfo = product?.priceInfo.find((item: any) => item.interval !== "month");
    onProductSelect({ ...productTemp, interval: paymentCycle });
  }

  const stuff = async () => {
    const db = getFirestore();
    // create a query object
    const q = query(
      collection(db, 'products'),
      where('active', '==', true),
    );

    let tempCoreData: PlanData | undefined = undefined;
    let tempProData: PlanData | undefined = undefined;

    const querySnapshot = await getDocs(q);
    const productsPromises = querySnapshot.docs.map(async (productDoc, index) => {
      if (productDoc.id === PlanId.Core || productDoc.id === PlanId.Pro) {
        let productInfo = productDoc.data();
        const pricesCollection = collection(productDoc.ref, 'prices');
        const priceQuerySnapshot = await getDocs(pricesCollection);

        const priceInfo: DocumentData[] = [];
        // eslint-disable-next-line array-callback-return
        priceQuerySnapshot.docs.map((priceDoc, index) => {
          priceInfo[index] = priceDoc.data();
          priceInfo[index]['id'] = priceDoc.id;
          productInfo['priceId'] = priceDoc.id;
        });
        productInfo['priceInfo'] = priceInfo;

        if (productDoc.id === PlanId.Core) {
          const tempCoreData = {
            planType: PlanType.Core,
            carouselIndex: SubscriptionCarouselIndex.Core,
            isCurrent: currentPlan === PlanType.Core && currentPaymentCycle === paymentCycle,
            label: t('general.plan.core'),
            description: t('pricing.plansPage.cards.core.description'),
            color: "#FFE9CC",
            price: {
              [PaymentCycle.Monthly]: `$${productInfo.priceInfo.find((item: any) => item.interval === "month").unit_amount / 100}`,
              [PaymentCycle.Quarterly]: "N/A",
              [PaymentCycle.Yearly]: `$${productInfo.priceInfo.find((item: any) => item.interval !== "month").unit_amount / 100}`,
            },
            numberUsers: t('pricing.general.startingAtUsers', { numberUsers: UserLimit.Core }),
            buttonText: coreDataButtonText,
            onButtonClick: coreDataOnButtonClick,
            productToSelect: productInfo,
            benefitsTitle: t('pricing.plansPage.cards.core.benefitsTitle'),
            benefits: [
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.users', { numberUsers: UserLimit.Core }),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.wellnessPrograms', { numberWellnessPrograms: LicenseLimit.Core }),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.mobile_app'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.reports'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.platform'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.add_ons'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.course'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.integrations'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.support'),
              },
              {
                icon: crossElement,
                text: t('pricing.plansPage.cards.benefits.customer_success_manager'),
              },
            ],
          };

          setCoreData(tempCoreData);
        }
        else if (productDoc.id === PlanId.Pro) {
          const tempProData = {
            planType: PlanType.Pro,
            carouselIndex: SubscriptionCarouselIndex.Pro,
            isCurrent: currentPlan === PlanType.Pro && currentPaymentCycle === paymentCycle,
            label: t('general.plan.pro'),
            description: t('pricing.plansPage.cards.pro.description'),
            color: "#8BE3CE",
            price: {
              [PaymentCycle.Monthly]: `$${productInfo.priceInfo.find((item: any) => item.interval === "month").unit_amount / 100}`,
              [PaymentCycle.Quarterly]: "N/A",
              [PaymentCycle.Yearly]: `$${productInfo.priceInfo.find((item: any) => item.interval !== "month").unit_amount / 100}`,
            },
            numberUsers: t('pricing.general.startingAtUsers', { numberUsers: UserLimit.Pro }),
            buttonText: proDataButtonText,
            onButtonClick: proDataOnButtonClick,
            productToSelect: productInfo,
            benefitsTitle: t('pricing.plansPage.cards.pro.benefitsTitle'),
            benefits: [
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.users', { numberUsers: UserLimit.Pro }),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.wellnessPrograms', { numberWellnessPrograms: LicenseLimit.Pro }),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.mobile_app'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.reports'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.platform'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.add_ons'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.course'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.integrations'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.support'),
              },
              {
                icon: checkmarkElement,
                text: t('pricing.plansPage.cards.benefits.customer_success_manager'),
              },
            ],
          };

          setProData(tempProData);
        }

        return productInfo;
      }
      else if (productDoc.id === AddOnId.User.toString() || productDoc.id === AddOnId.License.toString() || productDoc.id === AddOnId.Organization.toString()) {
        let productInfo = productDoc.data();
        const pricesCollection = collection(productDoc.ref, 'prices');
        const priceQuerySnapshot = await getDocs(pricesCollection);

        const priceInfo: DocumentData[] = [];
        // eslint-disable-next-line array-callback-return
        priceQuerySnapshot.docs.map((priceDoc, index) => {
          priceInfo[index] = priceDoc.data();
          priceInfo[index]['id'] = priceDoc.id;
          productInfo['priceId'] = priceDoc.id;
        });
        productInfo['priceInfo'] = priceInfo;

        const addOnPrice = (productInfo?.priceInfo.find((item: any) => item.interval === "month" && item.active).unit_amount / 100).toFixed(2);

        if (productDoc.id === AddOnId.User.toString()) {
          setAddOnPrices((prices) => ({ ...prices, ...{ user: addOnPrice } }));
        }
        else if (productDoc.id === AddOnId.License.toString()) {
          setAddOnPrices((prices) => ({ ...prices, ...{ license: addOnPrice } }));
        }
        else if (productDoc.id === AddOnId.Organization.toString()) {
          setAddOnPrices((prices) => ({ ...prices, ...{ organization: addOnPrice } }));
        }
        return productInfo;
      }
    });

    await Promise.all(productsPromises);

    setPlanDataArr([trialData, tempCoreData, tempProData, enterpriseData]);
  }

  useEffect(() => {
    stuff();
  }, [])

  useEffect(() => {
    if (coreData && proData) {
      coreData.isCurrent = currentPlan === PlanType.Core && currentPaymentCycle === paymentCycle;
      proData.isCurrent = currentPlan === PlanType.Pro && currentPaymentCycle === paymentCycle;

      setPlanDataArr([trialData, coreData, proData, enterpriseData]);
    }
  }, [coreData, currentPaymentCycle, currentPlan, paymentCycle, proData])

  useEffect(() => {
    const tempCards: { element: React.ReactElement, planType: PlanType }[] = [];

    planDataArr.forEach((pd, index) => {
      if (pd) {
        let card = {
          element: <></>,
          planType: pd.planType,
        }

        card.element = (
          <div key={`card-${card.planType}`} className={`${COMPONENT_CLASS}__card`} style={{ background: "#FAFAFA" }}>
            <div className="w-full h-4 rounded-t-xl" style={{ backgroundColor: pd.color }}></div>
            <div className="px-4 py-4">
              <h2>{pd.label}</h2>
              <p className="mb-4" style={{ minHeight: "104px", fontSize: "12px", fontWeight: "300" }}>{pd.description}</p>
              <div>
                <div className="flex gap-1 mb-4 items-center text-center justify-center">
                  <span className="text-32 font-bold">{pd.price[paymentCycle]}</span>
                  {paymentCycle === PaymentCycle.Monthly &&
                    pd.planType !== PlanType.Enterprise &&
                    pd.planType !== PlanType.Trial
                    ?
                    <p className="text-xs text-black-gray-50"> {t('pricing.general.month')}</p>
                    :
                    pd.planType !== PlanType.Enterprise &&
                    pd.planType !== PlanType.Trial &&
                    <p className="text-xs text-black-gray-50"> {t('pricing.general.year')}</p>
                  }
                </div>
                <div className="text-xs font-light text-center">
                  {pd.numberUsers}
                </div>
                <div className="my-4">
                  <Button
                    buttonStyle="primary"
                    buttonWidth="full"
                    buttonText={pd.buttonText}
                    disabled={pd.isCurrent}
                    onClick={() => {
                      if (!isUpgrade && !isLeadCapture) {
                        handleSignout();
                      }

                      if (pd.onButtonClick) {
                        pd.onButtonClick();
                      }
                      else if (pd.productToSelect) {
                        selectProduct(pd.productToSelect);
                      }
                    }} />
                </div>
                <div className="text-xs font-medium mb-2">
                  {pd.benefitsTitle}
                </div>
                <div className="text-xs font-light flex flex-col gap-2">
                  {pd.benefits.map((b) =>
                    <div className="flex gap-1 text-left">
                      <div>
                        {b.icon}
                      </div>
                      <div>
                        {b.text}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div >
        );

        tempCards.push(card);
      }
    })

    setCards(tempCards);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentCycle, planDataArr])

  const [faqs, setFaqs] = useState([
    {
      question: t('pricing.plansPage.faq.q_0'),
      answer: t('pricing.plansPage.faq.a_0'),
      isOpen: false
    },
    {
      question: t('pricing.plansPage.faq.q_1'),
      answer: t('pricing.plansPage.faq.a_1'),
      isOpen: false
    },
    {
      question: t('pricing.plansPage.faq.q_2'),
      answer: t('pricing.plansPage.faq.a_2'),
      isOpen: false
    },
    {
      question: t('pricing.plansPage.faq.q_3'),
      answer: t('pricing.plansPage.faq.a_3'),
      isOpen: false
    },
    {
      question: t('pricing.plansPage.faq.q_4'),
      answer: t('pricing.plansPage.faq.a_4'),
      isOpen: false
    },
    {
      question: t('pricing.plansPage.faq.q_5'),
      answer: t('pricing.plansPage.faq.a_5'),
      isOpen: false
    },
    {
      question: t('pricing.plansPage.faq.q_6'),
      answer: t('pricing.plansPage.faq.a_6'),
      isOpen: false
    },
  ]);

  const onCarouselChange = (index: number, item: React.ReactNode) => {
    setCarouselItemIndex(index);
  };

  const handleSignout = () => {
    authLogout();
    setState((state) => ({ ...state, ...{ authenticated: false, user: null } }));
  }

  const discountBadge = <Badge text={t('pricing.plansPage.discount')} bgColor="#00DFBC" textColor="#121622" />

  const enterpriseCard = (
    <div className="rounded-xl my-10" style={{ background: "#FAFAFA" }}>
      <div className="w-full h-4 rounded-t-xl" style={{ backgroundColor: enterpriseData.color }}></div>
      <div className="flex gap-4 justify-between items-center px-6 pt-4 pb-6">
        <div>
          <h2>{enterpriseData.label}</h2>
          <p>{enterpriseData.description}</p>
        </div>
        <div>
          <div>
            <Button
              buttonText={enterpriseData.buttonText}
              onClick={() => {
                if (enterpriseData.onButtonClick) {
                  enterpriseData.onButtonClick();
                }
              }} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="w-full flex justify-center py-10">
        <AppContentTypeToggle
          isOption1={paymentCycle === PaymentCycle.Monthly}
          isOption2={paymentCycle === PaymentCycle.Yearly}
          onClickOption1={() => setPaymentCycle(PaymentCycle.Monthly)}
          onClickOption2={() => setPaymentCycle(PaymentCycle.Yearly)}
          textOption1={t('pricing.plansPage.sl_montly')}
          textOption2={t('pricing.plansPage.sl_yearly')}
          extraElementOption2={discountBadge}
        />
      </div>
      <div className={`${COMPONENT_CLASS}__plans-info-desktop`}>
        <div className={`${COMPONENT_CLASS}__container-cards`}>
          {cards.map((card) => {
            if (!isUpgrade && card.planType === PlanType.Enterprise) {
              return <></>;
            }

            return card.element
          })}
        </div>

        {isUpgrade ? <></> : enterpriseCard}

        <Accordion
          isAccordionOpen={false}
          title={t('pricing.plansPage.tableContent.accordionTitle')}
          isStyle2={true}
          content={
            <PlansTableContent
              carouselItemIndex={carouselItemIndex}
              setCarouselItemIndex={setCarouselItemIndex}
              paymentCycle={paymentCycle}
              setPaymentCycle={setPaymentCycle}
              addOnPrices={addOnPrices}
              planDataArr={planDataArr}
              selectProduct={selectProduct}
              isMobile={false}
              isUpgrade={isUpgrade}
            />
          }
        />
      </div>
      <div className={`${COMPONENT_CLASS}__plans-info-mobile`}>
        <Carousel showArrows={true} showStatus={false} onChange={onCarouselChange} selectedItem={carouselItemIndex}>
          {cards.map((card, index) => {
            return (
              <div className={`${COMPONENT_CLASS}__card-wrapper`} key={`card-mobile-${card.planType}`}>
                {card.element}
              </div>
            )
          })}
        </Carousel>

        {isUpgrade ? <></> : enterpriseCard}

        <Accordion
          isAccordionOpen={false}
          title={t('pricing.plansPage.tableContent.accordionTitle')}
          isStyle2={true}
          content={
            <PlansTableContent
              carouselItemIndex={carouselItemIndex}
              setCarouselItemIndex={setCarouselItemIndex}
              setPaymentCycle={setPaymentCycle}
              paymentCycle={paymentCycle}
              addOnPrices={addOnPrices}
              planDataArr={planDataArr}
              selectProduct={selectProduct}
              isMobile={true}
              isUpgrade={isUpgrade}
            />
          }
        />
      </div >

      {!isUpgrade &&
        <div className="mt-10">
          <h2 className="text-center mb-6">{t('pricing.plansPage.faq.title')}</h2>
          <FaqAccordion faqs={faqs} setFaqs={setFaqs} />
        </div>
      }

      <div className="py-16 text-center">
        <h2 style={{ textAlign: "center" }}>{t('assessmentResults.reviews.title')}</h2>
        <p className="mt-2 mb-6 text-center">{t('assessmentResults.reviews.text')}</p>
        <Reviews />
      </div>

      <Modal
        isOpen={contactUsEnterpriseOpen}
        isLoading={false}
        onClose={() => {
          setContactUsEnterpriseOpen(false);
        }}
      >
        <EnterpriseContactUs title={t('pricing.plansPage.enterprise_salesTeam')} isEnterprise={currentPlan === PlanType.Enterprise} />
      </Modal>
    </>
  );
}

export default Plans;

